import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import LoadingIndicator from '../Components/LoadingIndicator';

const CustomerMasterView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    if (id) {
      fetchCustomer();
    }
  }, [id]);

  const fetchCustomer = async () => {
    if (!API_URL) {
      toast.error('API URL not configured.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(
        API_URL,
        { action: 'getCustomer', customerId: id },
        { headers: { 'Content-Type': 'application/json' } }
      );
      setCustomer(response.data);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        'Failed to load customer';
      toast.error(message);
      navigate('/customer-master');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getCreatedBy = () => {
    const by = customer?.createdBy;
    if (!by) return '-';
    if (typeof by === 'string') return by;
    if (typeof by === 'object') return by.name || by.email || by._id || '-';
    return String(by);
  };

  const getUpdatedBy = () => {
    const by = customer?.updatedBy;
    if (!by) return '-';
    if (typeof by === 'string') return by;
    if (typeof by === 'object') return by.name || by.email || by._id || '-';
    return String(by);
  };

  if (isLoading) {
    return (
      <div className="employee-form">
        <div className="employee-loading">
          <LoadingIndicator size={120} />
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="employee-form">
        <div className="employee-empty">Customer not found.</div>
      </div>
    );
  }

  return (
    <div className="employee-form">
      <div className="content-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <h1>Customer Details</h1>
            <p>View customer information.</p>
          </div>
          <div className="col-sm-6 text-right subscription-header-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/customer-master')}
            >
              Back
            </button>
          </div>
        </div>
      </div>

      <div className="card employee-content-card">
        <div className="card-body">
          <div className="detail-section">
            <div className="detail-section-header">
              <i className="fas fa-building"></i>
              <span>Customer Information</span>
            </div>
            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-building"></i>
                </div>
                <div className="detail-content">
                  <span className="detail-label">Company Name</span>
                  <span className="detail-value">{customer.companyName || '-'}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-file-invoice"></i>
                </div>
                <div className="detail-content">
                  <span className="detail-label">GST</span>
                  <span className="detail-value">{customer.gst || '-'}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-user"></i>
                </div>
                <div className="detail-content">
                  <span className="detail-label">Contact Person</span>
                  <span className="detail-value">{customer.contactPerson || '-'}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-envelope"></i>
                </div>
                <div className="detail-content">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{customer.email || '-'}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-phone"></i>
                </div>
                <div className="detail-content">
                  <span className="detail-label">Mobile Number</span>
                  <span className="detail-value">{customer.mobileNumber || '-'}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-map"></i>
                </div>
                <div className="detail-content">
                  <span className="detail-label">Address</span>
                  <span className="detail-value">{customer.address || '-'}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-city"></i>
                </div>
                <div className="detail-content">
                  <span className="detail-label">City</span>
                  <span className="detail-value">{customer.city || '-'}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <div className="detail-content">
                  <span className="detail-label">State</span>
                  <span className="detail-value">{customer.state || '-'}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-map-pin"></i>
                </div>
                <div className="detail-content">
                  <span className="detail-label">Pincode</span>
                  <span className="detail-value">{customer.pincode || '-'}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-info-circle"></i>
                </div>
                <div className="detail-content">
                  <span className="detail-label">Status</span>
                  <span className={`status-badge ${customer.status === 'active' ? 'active' : 'inactive'}`}>
                    {customer.status || '-'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <div className="detail-section-header">
              <i className="fas fa-history"></i>
              <span>Audit Trail</span>
            </div>
            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-user-plus"></i>
                </div>
                <div className="detail-content">
                  <span className="detail-label">Created By</span>
                  <span className="detail-value">{getCreatedBy()}</span>
                  <span className="detail-value" style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                    {formatDate(customer.createdAt)}
                  </span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-user-edit"></i>
                </div>
                <div className="detail-content">
                  <span className="detail-label">Updated By</span>
                  <span className="detail-value">{getUpdatedBy()}</span>
                  <span className="detail-value" style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                    {formatDate(customer.updatedAt)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="detail-actions">
            <button
              type="button"
              className="btn btn-primary btn-lg"
              onClick={() => navigate(`/customer-master-edit/${customer._id}`)}
            >
              <i className="fas fa-edit"></i>
              Edit Customer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerMasterView;
